// * IMPORTS *
// * basic imports (react & components)
import { CustomInput as InputForm } from "@/components/Input";
import { ModalLogin } from "@/components/Modal";
import { useAuth } from "@/contexts";
import Combobox from "react-widgets/Combobox";
import { chakra } from "@chakra-ui/react";

const ComboboxChakra = chakra(Combobox);

import { useAsyncHasRegister } from "@/hooks/Auth/useAsyncHasRegister";
import { useCorporateLocality } from "@/hooks/Client/useCorporateLocality";
import { useRegisterPJClient } from "@/hooks/Client/useRegisterPJClient";
import { useIsomorphicLayoutEffect } from "@/hooks/Custom";
import { Corporate as CorporateObject } from "@/types";
import { useEmpty, useFilter } from "@/utils";
import { useDate, useSintegraRegistrations } from "@/utilsV2";
import { useCorporate } from "@/utilsV2/useCorporate";

import {
  Box,
  Button,
  Flex,
  Img,
  InputGroup,
  Text,
  useBoolean,
  useBreakpointValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import Router from "next/router";
import { useEffect, useRef, useState } from "react";
import InputComponentEmail from "../Input/inputComponentEmail";

// * Main function *
export function FormPJ({ checkout, customWidth }: { checkout: boolean; customWidth: string }) {
  // * Setup initial states of application
  const [Document, setDocument] = useState(String); // * CNPJ
  const [Email, setEmail] = useState(String); // * Email
  const [Corporate, setCorporate] = useState(String); // * Razão Social
  const [FoundedAt, setFoundedAt] = useState(String); // * Data de fundação
  const [Registration, setRegistration] = useState(String); // * Inscrição Estadual
  const [Phone, setPhone] = useState(String); // * Phone
  const [Celphone, setCelphone] = useState(String); // * Celphone
  const [Password, setPassword] = useState(String); // * Password
  const [ConfirmPassword, setConfirmPassword] = useState(String); // * Password again
  const [Zipcode, setZipcode] = useState(String); // * Zipcode
  const [Street, setStreet] = useState(String); // * Street
  const [Number, setNumber] = useState(String); // * Number
  const [Complement, setComplement] = useState(String); // * Complement
  const [District, setDistrict] = useState(String); // * District
  const [City, setCity] = useState(String); // * City
  const [State, setState] = useState(String); // * State
  const [CodIbge, setCodIbge] = useState(String); // * CodIbge
  const [RegistrationIsDisable, setRegistrationIsDisable] = useState<boolean>(true);
  const [ShowPassword, setShow] = useBoolean();

  // * Setup states of application
  const [CorporateData, setCorporateData] = useState<CorporateObject>(null); // * Dados da empresa

  //* Toast default options
  const Toast = useToast({
    isClosable: true,
    position: "bottom",
    duration: 5000
  });

  // * Breakpoint controller
  const WideVersion = useBreakpointValue({
    base: false,
    xs: false,
    sm: false,
    md: false,
    lg: true,
    xl: true
  });

  // * Modal controllers
  const { isOpen, onOpen, onClose } = useDisclosure();

  // * Hooks to Auth methods
  const { signIn } = useAuth();
  // * General controllers and Utils
  const [HasRegister, setHasRegister] = useState(true);
  const [Disable, setDisable] = useState(true);
  const [SubmitDisable, setSubmitDisable] = useState(true);
  const [SubmitLoading, setSubmitLoading] = useState(false);
  const [ForwardDisable, setForwardDisable] = useState(true);
  const [ForwardLoading, setForwardLoading] = useState(false);
  const [ExistEmail, setExistEmail] = useState(false);
  const [ExistDoc, setExistDoc] = useState(false);
  const [ExistMsg, setExistMsg] = useState("");
  const emailRegex = /@(.+)?/gim;

  const emailAutoCompleteRef = useRef<any>(null);

  // * Make Forward button enabled
  useIsomorphicLayoutEffect(() => {
    setForwardLoading(true);
    let DocumentFiltered = useFilter(Document ?? "", { type: "number" });
    if (
      !useEmpty(Document) &&
      DocumentFiltered.length === 14 &&
      !useEmpty(Email) &&
      Email.length > 5 &&
      Email.indexOf("@") !== -1
    ) {
      setForwardDisable(false);
      setSubmitDisable(false);
    } else {
      setForwardDisable(true);
      setSubmitDisable(true);
    }
    setForwardLoading(false);
  }, [Document, Email]);

  // * Consume Corporate Data
  useEffect(() => {
    // console.log("Document", Document);
    if (CorporateData) {
      setForwardLoading(true);
      const Sintegra = useSintegraRegistrations(
        CorporateData.sintegra,
        CorporateData.address.state,
        CorporateData.secondary_activities.concat(CorporateData.primary_activity),
        Document
      );

      if (Sintegra.error) {
        Toast({
          title: Sintegra.errorMessage,
          status: "error",
          containerStyle: {
            color: "white",
            fontFamily: "arial",
            fontWeight: "100"
          }
        });
        setRegistrationIsDisable(false);
      }

      setCorporate(CorporateData.name);
      setFoundedAt(useDate(CorporateData.founded, "LATAM"));
      setRegistration(Sintegra.ie);
      setPhone(CorporateData.phone);
      setCodIbge(CorporateData.address.city_ibge);

      useCorporateLocality({
        zip: CorporateData.address.zip,
        street: CorporateData.address.street,
        number: CorporateData.address.number,
        details: CorporateData.address.details,
        neighborhood: CorporateData.address.neighborhood,
        city: CorporateData.address.city,
        state: CorporateData.address.state
      }).then((resp) => {
        setZipcode(resp.Zipcode);
        setStreet(resp.Street);
        setNumber(resp.Number);
        setComplement(resp.Complement);
        setDistrict(resp.District);
        setCity(resp.City);
        setState(resp.State);
        Toast({
          title: "CNPJ encontrado!",
          status: "success",
          duration: 3000,
          containerStyle: {
            color: "white",
            fontFamily: "arial",
            fontWeight: "100"
          },

          onCloseComplete: () => {
            Toast({
              title: "Atenção",
              description:
                "Pode ser que haja alguma inconsistência entre o CEP e o real endereço da sua empresa. Fique atento a possíveis alterações.",
              status: "error",
              containerStyle: {
                color: "white",
                fontFamily: "arial",
                fontWeight: "100"
              }
            });
          }
        });
        setForwardLoading(false);
      });
    }
    setForwardDisable(true);
  }, [CorporateData]);

  // * Restore corporate data
  const SyncCorporate = async () => {
    // * Trait docuemnt
    setForwardLoading(true);
    let DocFiltered = useFilter(Document, { type: "number" });

    // * Get corporate data
    const [CorporateData, CorporateError] = await useCorporate(DocFiltered);

    // * Check if has error
    if (CorporateError) {
      Toast({
        title: CorporateError[0],
        description: CorporateError[1] ?? "",
        status: "error",
        isClosable: true,
        containerStyle: {
          color: "white",
          fontFamily: "arial",
          fontWeight: "100"
        }
      });
      setForwardLoading(false);
      return;
    } else {
      setCorporateData(CorporateData);
    }
    setForwardLoading(false);
  };

  // * Verify if user Exists, if not, valid CPF and Email, if exists, show Modal
  const UserExists = async (): Promise<void> => {
    // * Call API to verify if user exists
    setForwardLoading(true);
    const [ExistsData, ExistsError] = await useAsyncHasRegister({
      UserEmail: Email,
      UserDocument: Document,
      isPhysicalPerson: true
    });

    // * If user has register
    if (ExistsData) {
      setExistMsg(ExistsData.msg);
      setExistEmail(ExistsData.email);
      setExistDoc(ExistsData.doc);
      setHasRegister(true);
      setForwardLoading(false);
      onOpen();

      return;
    }

    // * If Requisition has Error and user not exists
    if (ExistsError && ExistsData === null) {
      setHasRegister(false);
      setForwardLoading(false);
      return SyncCorporate();
    }
  };

  // * Submit form
  const SubmitForm = async (e: {
    preventDefault: () => void;
    stopPropagation: () => void;
  }): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    setSubmitLoading(true);
    // * Check if all are fine
    switch (true) {
      // * valid CNPJ
      case useEmpty(Document) || useFilter(Document, { type: "number" }).length !== 14:
        Toast({
          title: "Por favor, informe o CNPJ da sua empresa.",
          status: "error",
          duration: 5000,
          containerStyle: {
            color: "white",
            fontFamily: "arial",
            fontWeight: "100"
          }
        });
        setSubmitLoading(false);
        return;
      // * valid Email
      case useEmpty(Email) || Email.length < 5 || Email.indexOf("@") === -1:
        Toast({
          title: "Por favor, informe um email válido.",
          status: "error",
          duration: 5000,
          containerStyle: {
            color: "white",
            fontFamily: "arial",
            fontWeight: "100"
          }
        });
        setSubmitLoading(false);
        return;
      // * valid if has a Phone or where at Celphone
      case useEmpty(Phone) && useEmpty(Celphone):
        Toast({
          title: "Por favor, informe um telefone ou celular.",
          status: "error",
          duration: 5000,
          containerStyle: {
            color: "white",
            fontFamily: "arial",
            fontWeight: "100"
          }
        });
        setSubmitLoading(false);
        return;
      // valid password and confirm password
      case useEmpty(Password) || Password !== ConfirmPassword || Password.length < 6:
        Toast({
          title: "Há algum problema com sua senha.",
          description:
            "Sua senha precisa ter no mínimo 6 caracteres, e a Confirmação de senha precisa ser igual a senha.",
          status: "error",
          duration: 5000,
          containerStyle: {
            color: "white",
            fontFamily: "arial",
            fontWeight: "100"
          }
        });
        setSubmitLoading(false);
        return;
      // * valid if has a Zipcode
    }

    const phoneNum = Phone.replace(/[^0-9]/g, "");
    const cellphoneNum = Celphone.replace(/[^0-9]/g, "");

    // * Call API to register user
    const [RegisterData, RegisterError] = await useRegisterPJClient({
      Corporate: CorporateData,
      Data: {
        cnpj: Document,
        email: Email,
        name: Corporate,
        date: FoundedAt,
        registration: Registration,
        phone: phoneNum,
        celphone: cellphoneNum,
        password: Password,
        zipcode: Zipcode,
        street: Street,
        number: Number,
        complement: Complement,
        district: District,
        city: City,
        state: State,
        CodIbge: CodIbge
      }
    });

    // * If Requisition has error
    if (RegisterError) {
      setSubmitLoading(false);
      Toast({
        title: RegisterError[0],
        description: RegisterError[1],
        status: "error",
        duration: 5000,
        containerStyle: {
          color: "white",
          fontFamily: "arial",
          fontWeight: "100"
        }
      });
      setSubmitDisable(false);
      return;
    } else {
      // * If Requisition has data, trait this
      await signIn({
        password: Password,
        UserEmail: Email
      })
        .then(({ status, message }) => {
          if (status) {
            Toast({
              title: "Usuário criado com sucesso!",
              description: checkout
                ? "Aguarde para finalizar a compra"
                : "Aguarde para ser redirecionado",
              status: "success",
              containerStyle: {
                color: "white",
                fontFamily: "arial",
                fontWeight: "100"
              },
              onCloseComplete: () => {
                Router.push(checkout ? "/checkout/entrega" : "/minhaconta");
              }
            });
          } else {
            throw new Error(message);
          }
        })
        .catch(() => {
          Toast({
            title: "Cadastro realizado com sucesso",
            description: "Por gentileza realize o login na tela que se abrirá",
            status: "success",
            containerStyle: {
              color: "white",
              fontFamily: "arial",
              fontWeight: "100"
            },
            onCloseComplete: () => {
              onOpen();
            }
          });
        })
        .finally(() => {
          setSubmitLoading(false);
          setSubmitDisable(false);
          setSubmitLoading(false);
        });
      return;
    }
  };

  // Main return statement
  return (
    <>
      {/* Main content */}
      <Flex direction="column" as="form" w={["100%", customWidth]} onSubmit={SubmitForm}>
        {/* FORM FIELDS */}
        {/* CPF &~ FORWARD BUTTON */}
        <Flex direction={["column", "row"]} mb="1rem" w={"full"}>
          <InputForm
            name="Document"
            p={3}
            py={2}
            value={Document}
            onChange={(e: { target: { value: string } }) => {
              setDocument(e.target.value);
            }}
            placeholder="CNPJ"
            id={"RegisterDocumentPJ"}
            textStyle={"Regular"}
            border={"1px solid #A9A9A9"}
            borderRadius={"50px"}
            type="tel"
            mask={"99.999.999/9999-99"}
            width={"full"}
          />
        </Flex>

        {/* EMAIL */}
        <Flex direction={"column"} mb={["1rem", "1.5625rem"]}>
          <InputComponentEmail email={Email} setEmail={setEmail} />

          {WideVersion && (
            <Button
              variant={"primary-button"}
              bg={"#121212"}
              w="full"
              mt={"1rem"}
              h={"40px"}
              isLoading={ForwardLoading}
              isDisabled={ForwardDisable}
              onClick={UserExists}
              loadingText="Verificando..."
              _loading={{ color: "white" }}
              _hover={{ opacity: "0.5" }}
            >
              <Text color="white">{"VERIFICAR"}</Text>
            </Button>
          )}
        </Flex>

        {!WideVersion && (
          <Button
            variant={"primary-button"}
            bg={"#121212"}
            textColor={"white"}
            height="40px"
            w="full"
            type="button"
            isLoading={ForwardLoading}
            isDisabled={ForwardDisable}
            onClick={UserExists}
            loadingText="Verificando..."
            _loading={{ color: "white" }}
          >
            <Text color="white">{"VERIFICAR"}</Text>
          </Button>
        )}

        <Box position={"relative"}>
          <Box
            display={HasRegister ? "block" : "none"}
            zIndex={400}
            w={"full"}
            h={"full"}
            bg={"white"}
            opacity={0.6}
            position={"absolute"}
          ></Box>
          {/* PERSON TITLE */}
          <Flex my="1rem">
            <Text
              fontSize="h3"
              color={HasRegister ? "gray.300" : "ll-blue.main"}
              letterSpacing={"-1px"}
            >
              Identificação
            </Text>
          </Flex>
          {/* NAME & BIRTHDATE */}
          <Flex direction={["column", "row"]} mb="1rem" gap="1rem" w={"full"}>
            <InputForm
              border={"1px solid #ECECEC"}
              borderRadius={"50px"}
              name="Name"
              id={"RegisterNamePJ"}
              p={3}
              py={2}
              value={Corporate}
              onChange={(e: { target: { value: string } }) => {
                setCorporate(e.target.value);
              }}
              placeholder="Razão Social"
              type="text"
              width={["full", "full"]}
              isDisabled={true}
            />
          </Flex>
          <Flex direction={["column", "row"]} mb="1rem" gap="1rem" w={"full"}>
            <InputForm
              border={"1px solid #ECECEC"}
              borderRadius={"50px"}
              p={3}
              py={2}
              name="Birthdate"
              id={"RegisterBirthPJ"}
              value={FoundedAt}
              onChange={(e: { target: { value: string } }) => {
                setFoundedAt(e.target.value);
              }}
              placeholder="Fundação"
              isDisabled={true}
              type="tel"
              mask={"99/99/9999"}
              width={["full", "50%"]}
            />
            <InputForm
              border={"1px solid #ECECEC"}
              borderRadius={"50px"}
              p={3}
              py={2}
              name="Registration"
              id={"RegisterRegistrationPJ"}
              value={Registration}
              onChange={(e: { target: { value: string } }) => {
                setRegistration(e.target.value);
              }}
              placeholder="Inscrição"
              isDisabled={RegistrationIsDisable}
              type="text"
              width={["full", "50%"]}
            />
          </Flex>
          {/* PHONE & CELPHONE */}
          <Flex mb="1rem" gap="1rem">
            <InputForm
              border={"1px solid #ECECEC"}
              borderRadius={"50px"}
              p={3}
              py={2}
              name="Phone"
              id={"RegisterPhonePJ"}
              mask={Phone && Phone.split("")[5] === "9" ? `(99) 99999-9999` : `(99) 9999-9999`}
              value={Phone}
              onChange={(e: { target: { value: string } }) => {
                setPhone(e.target.value);
              }}
              placeholder="Telefone"
              fontSize={!WideVersion && "16px"}
              type="tel"
              width={["50%", "50%"]}
              isDisabled={HasRegister}
            />
            <InputForm
              border={"1px solid #ECECEC"}
              borderRadius={"50px"}
              p={3}
              py={2}
              name="Celphone"
              id={"RegisterCellPhonePJ"}
              mask={
                Celphone && Celphone.split("")[5] === "9" ? `(99) 99999-9999` : `(99) 9999-9999`
              }
              value={Celphone}
              onChange={(e: { target: { value: string } }) => {
                setCelphone(e.target.value);
              }}
              fontSize={!WideVersion && "16px"}
              placeholder="celular"
              type="tel"
              width={["50%", "50%"]}
              isDisabled={HasRegister}
            />
          </Flex>

          {/* PASSWORD AND CONFIMATION */}
          <Flex direction={["column", "column"]} mb=".7rem" gap="1rem" w={"full"}>
            <InputGroup
              display="flex"
              alignItems="center"
              border="1px solid"
              borderColor="#58585A"
              borderRadius={"50px"}
              fontSize={"16px"}
            >
              <InputForm
                border={"0px"}
                borderRadius={"50px"}
                name="Password"
                borderColor={"white"}
                // borderRightColor={"#A9A9A9"}
                // borderColor={Password.length > 5 ? "" : "#FF003A"}
                id={"RegisterPasswordPF"}
                value={Password}
                p={3}
                py={2}
                onChange={(e: { target: { value: string } }) => {
                  setPassword(e.target.value);
                }}
                placeholder="Senha"
                type={ShowPassword ? "text" : "password"}
                width={["full", "full"]}
                isDisabled={HasRegister}
              ></InputForm>
              <Img
                mx={["10px", "10px"]}
                w={["25px", "25px"]}
                alt={"Alternative to Image"}
                src={
                  ShowPassword
                    ? "https://cdn.pneufree.com.br/LINGLONG/images/Site/icons/geral/password-hidden.svg"
                    : "https://cdn.pneufree.com.br/LINGLONG/images/Site/icons/geral/password-nohidden.svg"
                }
                onClick={setShow.toggle}
                _focus={{ border: "none" }}
              />
            </InputGroup>

            <InputGroup
              display="flex"
              alignItems="center"
              border="1px solid"
              borderColor="#58585A"
              borderRadius={"50px"}
              fontSize={"16px"}
            >
              <InputForm
                border={"1px solid #ECECEC"}
                borderRadius={"50px"}
                borderColor={"white"}
                name="ConfirmPassword"
                id={"RegisterPassConfirmPF"}
                value={ConfirmPassword}
                p={3}
                py={2}
                onChange={(e: { target: { value: string } }) => {
                  setConfirmPassword(e.target.value);
                }}
                placeholder="Confirmar senha"
                type={"password"}
                isDisabled={HasRegister}
                width={["full", "full"]}
              />
              <Img
                mx={["10px", "10px"]}
                w={["25px", "25px"]}
                alt={"Alternative to Image"}
                src={
                  ShowPassword
                    ? "https://cdn.pneufree.com.br/LINGLONG/images/Site/icons/geral/password-hidden.svg"
                    : "https://cdn.pneufree.com.br/LINGLONG/images/Site/icons/geral/password-nohidden.svg"
                }
                onClick={setShow.toggle}
                _focus={{ border: "none" }}
              />
            </InputGroup>
          </Flex>

          {/* ADDRESS TITLE */}
          <Flex my="1rem">
            <Text
              fontSize="h3"
              color={HasRegister ? "gray.300" : "ll-blue.main"}
              letterSpacing={"-1px"}
            >
              {"Endereço"}
            </Text>
          </Flex>

          {/* ZIPCODE & DESCRIPTION */}
          <Flex my="1rem" gap="1rem" w={"full"} alignItems={"start"} direction={["row", "column"]}>
            <InputForm
              border={"1px solid #ECECEC"}
              borderColor={Zipcode.length > 0 ? "" : "#FF003A"}
              borderRadius={"50px"}
              name="Zipcode"
              id={"RegisterZipcodePF"}
              p={3}
              py={2}
              value={Zipcode}
              onChange={(e: { target: { value: string } }) => {
                setZipcode(e.target.value);
              }}
              placeholder="CEP"
              type="tel"
              width={["full", "full"]}
              mask={"99.999-999"}
              isDisabled={HasRegister}
            />
            <Text
              color="#58585A"
              fontSize={["text3", "11px"]}
              ml={"15px"}
              mt={WideVersion && "-10px"}
            >
              {"Insira seu CEP para preencher o endereço"}
            </Text>
          </Flex>

          {/* STREET, NUMBER & COPLEMENT */}
          <Flex direction={["column", "row"]} mb="1rem" gap="1rem">
            <InputForm
              name="Street"
              p={3}
              py={2}
              id={"RegisterStreetPJ"}
              value={Street}
              onChange={(e: { target: { value: string } }) => {
                setStreet(e.target.value);
              }}
              placeholder="Rua"
              type="text"
              minW="14.85rem"
              borderRadius={"50px"}
              width={["full", "20.85rem"]}
              isDisabled={true}
            />

            <Flex gap="1rem">
              <InputForm
                name="Number"
                p={3}
                py={2}
                value={Number}
                onChange={(e: { target: { value: string } }) => {
                  setNumber(e.target.value);
                }}
                id={"RegisterNumberPJ"}
                placeholder="Número"
                borderRadius={"50px"}
                type="tel"
                width={["full", "8.85rem"]}
                isDisabled={true}
              />
            </Flex>
          </Flex>

          {/* DISTRICT, CITY, STATE */}
          <Flex direction={["column", "row"]} mb="1rem" gap="1rem">
            <InputForm
              p={3}
              py={2}
              name="Complement"
              value={Complement}
              id={"RegisterCompPJ"}
              onChange={(e: { target: { value: string } }) => {
                setComplement(e.target.value);
              }}
              placeholder="Complemento"
              borderRadius={"50px"}
              type="text"
              width={["full", "50%"]}
            />
            <InputForm
              p={3}
              py={2}
              name="District"
              id={"RegisterDistrictPJ"}
              value={District}
              onChange={(e: { target: { value: string } }) => {
                setDistrict(e.target.value);
              }}
              placeholder="Bairro"
              borderRadius={"50px"}
              type="text"
              // minW="14.85rem"
              width={["full", "50%"]}
              isDisabled={true}
            />
          </Flex>
          <Flex direction={["column", "row"]} mb="1rem" gap="1rem">
            <InputForm
              p={3}
              py={2}
              name="City"
              id={"RegisterCityPJ"}
              value={City}
              onChange={(e: { target: { value: string } }) => {
                setCity(e.target.value);
              }}
              placeholder="Cidade"
              borderRadius={"50px"}
              type="text"
              width={["full", "70%"]}
              isDisabled={true}
            />
            <InputForm
              p={3}
              py={2}
              name="State"
              value={State}
              onChange={(e: { target: { value: string } }) => {
                setState(e.target.value);
              }}
              id={"RegisterStatePJ"}
              placeholder="Estado"
              borderRadius={"50px"}
              type="text"
              width={["full", "30%"]}
              isDisabled={true}
            />
          </Flex>

          {/* BUTTONS */}
          <Button
            variant={"primary-button"}
            w={"full"}
            h={"40px"}
            mb="2rem"
            type="submit"
            isLoading={SubmitLoading}
            isDisabled={SubmitDisable}
            _hover={{ opacity: "0.5" }}
            loadingText="Cadastrando..."
            _loading={{ color: "white" }}
          >
            <Text fontStyle="Bold" color="white">
              {"PROSSEGUIR"}
            </Text>
          </Button>
        </Box>
      </Flex>
      {/* Modal to user that has register */}
      <ModalLogin
        close={onClose}
        open={isOpen}
        doc={Document}
        isPF={false}
        email={Email}
        existDoc={ExistDoc}
        existEmail={ExistEmail}
        Msg={ExistMsg}
      />
    </>
  );
}
